import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/AdminDashboard`;

// ============================================================
// Servicio de Admin Dashboard (solo monitoreo, solo lectura)
// Controller: AdminDashboardController
// Ruta base: /api/AdminDashboard
// Autenticación: JWT Bearer (todos los endpoints)
// Total endpoints: 8
// Documentación: hipaa-logs/Docs/Control/14_ENDPOINTS_ADMIN_DASHBOARD.md
// ============================================================

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * Obtiene los 4 KPIs del bloque "User Statistics" del dashboard administrativo.
 * Valores siempre INT no nullables (cero si la BD está vacía).
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/user-stats
 *
 * Respuesta exitosa (200):
 * {
 *   "totalUsers": 92,
 *   "activeToday": 6,
 *   "newThisMonth": 1,
 *   "lockedAccounts": 0
 * }
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 * - 500 Internal Server Error: error SQL inesperado
 */
export const getAdminUserStats = async () => {
  try {
    const response = await axios.get(`${API_URL}/user-stats`, authHeaders());
    return response.data;
  } catch (error) {
    console.error("Error en getAdminUserStats:", error);
    throw error;
  }
};

/**
 * Obtiene la cantidad de usuarios activos agrupados por rol RBAC.
 * Devuelve TODOS los roles, activos e inactivos, ordenados alfabéticamente
 * por roleName. Roles sin usuarios devuelven usersCount = 0.
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/users-by-role
 *
 * Respuesta exitosa (200):
 * [
 *   { "roleId": 1, "roleName": "Admin", "roleKey": "admin", "status": 1, "usersCount": 3 },
 *   { "roleId": 3, "roleName": "Care Management", "roleKey": "care_management", "status": 1, "usersCount": 25 },
 *   { "roleId": 5, "roleName": "Old Auditor", "roleKey": "old_auditor", "status": 0, "usersCount": 4 }
 * ]
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminUsersByRole = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/users-by-role`,
      authHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Error en getAdminUsersByRole:", error);
    throw error;
  }
};

/**
 * Drill-down: lista de usuarios asignados a un rol específico con su última
 * fecha de ingreso. Usado al hacer clic en un chip de rol del dashboard.
 *
 * @param {number} roleId - ID del rol a consultar
 * @param {boolean} [includeInactive=false] - true permite consultar roles inactivos
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/users-by-role/3/detail?includeInactive=false
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "OK",
 *   "users": [
 *     {
 *       "employeeId": 12,
 *       "firstName": "Andre",
 *       "lastName": "Lopez",
 *       "fullName": "Andre Lopez",
 *       "mail": "andre.lopez@santeplus.com",
 *       "lastAccess": "2026-04-29T08:14:00",
 *       "daysSinceLastAccess": 0
 *     }
 *   ]
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Rol no encontrado o inactivo" }
 * { "result": 1, "message": "Rol no encontrado" }
 *
 * Notas:
 * - daysSinceLastAccess es null cuando el usuario nunca ha entrado.
 * - El frontend muestra "99+" cuando es null y aplica color rojo cuando es >= 8.
 */
export const getAdminUsersByRoleDetail = async (
  roleId,
  includeInactive = false
) => {
  try {
    const response = await axios.get(
      `${API_URL}/users-by-role/${roleId}/detail`,
      { ...authHeaders(), params: { includeInactive } }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 400 && error.response.data) {
      return error.response.data;
    }
    console.error("Error en getAdminUsersByRoleDetail:", error);
    throw error;
  }
};

/**
 * Obtiene los 3 KPIs del bloque "Audit Log (24h)" del dashboard.
 * Ventana móvil de 24 horas (no día calendario).
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/audit-summary-24h
 *
 * Respuesta exitosa (200):
 * {
 *   "totalEvents": 1520,
 *   "dataAccessEvents": 687,
 *   "loginAttempts": 198
 * }
 *
 * Notas:
 * - dataAccessEvents incluye IDs 28, 29, 30, 31 (categoría data_access).
 *   El frontend lo puede mostrar como "Patient Activity".
 * - loginAttempts incluye solo IDs 1 (login_success) y 2 (login_failed).
 *   No incluye locks/unlocks (3, 4, 5).
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminAuditSummary24h = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/audit-summary-24h`,
      authHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Error en getAdminAuditSummary24h:", error);
    throw error;
  }
};

/**
 * Devuelve los N eventos de auditoría más recientes para el feed compacto.
 * Eventos excluidos: IDs 28-31 (categoría data_access). Ventana: últimos 30 días.
 *
 * @param {number} [top=20] - Cantidad máxima de filas. Si se envía <= 0, el backend lo normaliza a 20.
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/recent-activity?top=20
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "eventDate": "2026-04-29T07:14:32",
 *     "eventKey": "employee_created",
 *     "category": "master_data",
 *     "employeeId": 12,
 *     "userMail": "david.linares@santeplus.com",
 *     "fullName": "David Linares",
 *     "detail": "New employee: John Smith"
 *   }
 * ]
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminRecentActivity = async (top = 20) => {
  try {
    const response = await axios.get(`${API_URL}/recent-activity`, {
      ...authHeaders(),
      params: { top },
    });
    return response.data;
  } catch (error) {
    console.error("Error en getAdminRecentActivity:", error);
    throw error;
  }
};

/**
 * Variante de recent-activity sin tope: devuelve TODOS los eventos dentro de
 * la ventana de 30 días. Misma estructura que getAdminRecentActivity.
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/all-activity
 *
 * Respuesta: idéntica a getAdminRecentActivity, sin tope.
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminAllActivity = async () => {
  try {
    const response = await axios.get(`${API_URL}/all-activity`, authHeaders());
    return response.data;
  } catch (error) {
    console.error("Error en getAdminAllActivity:", error);
    throw error;
  }
};

/**
 * Lista de usuarios actualmente conectados (sesión activa, no expirada,
 * dentro del límite de inactividad). Refresco recomendado: 30-60 segundos.
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/online-users
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeeId": 12,
 *     "firstName": "Andre",
 *     "lastName": "Lopez",
 *     "fullName": "Andre Lopez",
 *     "mail": "andre.lopez@santeplus.com",
 *     "loginDate": "2026-04-29T06:30:11",
 *     "lastActivity": "2026-04-29T14:23:47",
 *     "minutesSinceLastActivity": 0,
 *     "ipAddress": "192.168.1.45",
 *     "userAgent": "Mozilla/5.0 ..."
 *   }
 * ]
 *
 * Notas:
 * - lastActivity se actualiza cada 1-5 minutos por el middleware,
 *   minutesSinceLastActivity puede mostrar 0-5 para usuarios realmente activos.
 * - Si un usuario tiene múltiples sesiones, devuelve solo la más reciente.
 * - Orden: lastActivity DESC.
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminOnlineUsers = async () => {
  try {
    const response = await axios.get(`${API_URL}/online-users`, authHeaders());
    return response.data;
  } catch (error) {
    console.error("Error en getAdminOnlineUsers:", error);
    throw error;
  }
};

/**
 * Lista de usuarios sin actividad. Dos categorías: never_logged_in y
 * inactive_threshold (>= thresholdDays sin entrar).
 *
 * @param {Object} [params]
 * @param {number} [params.thresholdDays=7] - Umbral en días. Valores típicos: 7, 14, 30.
 * @param {string} [params.search] - Texto opcional (longitud mínima recomendada: 2 caracteres).
 *
 * Ejemplo de llamada:
 * GET /api/AdminDashboard/inactive-users?thresholdDays=7&search=carla
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeeId": 67,
 *     "firstName": "Carla",
 *     "lastName": "Diaz",
 *     "fullName": "Carla Diaz",
 *     "mail": "carla.diaz@santeplus.com",
 *     "jobPosition": "Lab Tech",
 *     "lastAccess": null,
 *     "daysSinceLastAccess": null,
 *     "userCreateDate": "2026-03-10T10:00:00",
 *     "group": "never_logged_in"
 *   }
 * ]
 *
 * Notas:
 * - El backend hace Trim() del search internamente.
 * - daysSinceLastAccess null = nunca ingresó. Frontend muestra "99+".
 * - Orden: never_logged_in primero, luego más inactivos primero.
 *
 * Posibles errores:
 * - 401 Unauthorized: token JWT inválido o ausente
 */
export const getAdminInactiveUsers = async ({
  thresholdDays = 7,
  search,
} = {}) => {
  try {
    const params = { thresholdDays };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const response = await axios.get(`${API_URL}/inactive-users`, {
      ...authHeaders(),
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error en getAdminInactiveUsers:", error);
    throw error;
  }
};
